import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, GraduationCap, Loader2, Send } from 'lucide-react';
import {
  MAX_MESSAGE_LENGTH,
  STUDENT_MESSAGING_REFRESH_MS,
  isRefusal,
  mergeLatestPage,
  prependOlderPage,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
  type ConversationKind,
  type StudentMessage,
  type StudentThread,
} from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';
import dayjs from '../lib/dates';
import {
  ErrorState,
  KIND_LABELS,
  Loading,
  OFFLINE,
  RefreshNotice,
  TEXT_ONLY,
  dayLabel,
  initials,
  secondaryButton,
} from './StudentMessageParts';

/** Un message du fil, à plat : auteur, heure, texte. Même présentation que dans l'espace principal. */
function Line({
  message,
  newDay,
}: {
  message: StudentMessage;
  /** Premier message de sa journée : un séparateur le précède. */
  newDay: boolean;
}) {
  return (
    <li
      className={`messaging-message${
        message.mine
          ? ' messaging-message--mine'
          : message.authorKind === 'student'
            ? ' messaging-message--student'
            : ''
      }`}
    >
      {newDay && (
        <div className="messaging-day">{dayLabel(message.createdAt)}</div>
      )}
      <div className="messaging-author-line">
        <span className="messaging-message-avatar" aria-hidden="true">
          {initials(message.author)}
        </span>
        <strong>{message.mine ? 'Vous' : message.author}</strong>
        <time dateTime={message.createdAt}>
          {dayjs(message.createdAt).format('HH:mm')}
        </time>
      </div>
      <p className="messaging-text">{message.body}</p>
    </li>
  );
}

/**
 * Fil d'une conversation avec un ou des élèves : messages, lecture signalée,
 * rafraîchissement, saisie. Texte seul : aucune des fonctions de la messagerie
 * de l'équipe (pièces jointes, réactions, liens métier, modification) n'y figure.
 */
export default function StudentMessageThread({
  conversationId,
  fallbackTitle,
  fallbackKind = 'DIRECT',
  onBack,
}: {
  conversationId: string;
  /** Titre et nature connus par la liste, en attendant la réponse du fil. */
  fallbackTitle?: string;
  fallbackKind?: ConversationKind;
  onBack: () => void;
}) {
  const userId = useAuthStore((s) => s.user?.id);
  const qc = useQueryClient();
  const threadKey = studentMessagingKeys.thread(userId, conversationId);
  const conversationsKey = studentMessagingKeys.conversations(userId);
  const [text, setText] = useState('');

  const thread = useQuery({
    queryKey: threadKey,
    queryFn: async () => {
      const latest = await studentMessagingApi.getMessages(conversationId);
      // Lu après la réponse : les messages précédents chargés entre-temps sont gardés.
      return mergeLatestPage(qc.getQueryData<StudentThread>(threadKey), latest);
    },
    refetchInterval: STUDENT_MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  // Accès refusé ou rien à montrer : le contenu est masqué.
  const blocked = thread.isError && (!thread.data || isRefusal(thread.error));
  const data = blocked ? undefined : thread.data;
  const items = data?.items;
  const oldest = items?.length ? items[0].sequence : 0;
  const newest = items?.length ? items[items.length - 1].sequence : 0;
  const lastIsMine = items?.length ? items[items.length - 1].mine : false;

  const older = useMutation({
    mutationFn: (before: number) =>
      studentMessagingApi.getMessages(conversationId, before),
    onSuccess: (page) =>
      qc.setQueryData<StudentThread>(threadKey, (shown) =>
        shown ? prependOlderPage(shown, page) : shown,
      ),
  });

  // Lecture : le fil est ouvert, on signale le dernier message affiché.
  const marked = useRef(0);
  const refreshedAt = thread.dataUpdatedAt;
  useEffect(() => {
    if (!newest || newest <= marked.current) return;
    const previous = marked.current;
    marked.current = newest;
    studentMessagingApi.markRead(conversationId, newest).then(
      () =>
        void qc.invalidateQueries({
          queryKey: studentMessagingKeys.conversations(userId),
        }),
      () => {
        // Nouvel essai au prochain rafraîchissement du fil.
        if (marked.current === newest) marked.current = previous;
      },
    );
  }, [newest, refreshedAt, conversationId, userId, qc]);

  // Un nouvel essai du même texte reprend son identifiant : pas de doublon.
  const pending = useRef({ signature: '', requestId: '' });
  const send = useMutation({
    mutationFn: (body: string) => {
      if (pending.current.signature !== body)
        pending.current = { signature: body, requestId: crypto.randomUUID() };
      return studentMessagingApi.sendMessage(conversationId, {
        requestId: pending.current.requestId,
        body,
      });
    },
    onSuccess: (sent) => {
      pending.current = { signature: '', requestId: '' };
      // L'envoi vaut lecture jusqu'à ce message, côté serveur.
      marked.current = Math.max(marked.current, sent.sequence);
      setText('');
      void qc.invalidateQueries({ queryKey: threadKey });
      void qc.invalidateQueries({ queryKey: conversationsKey });
    },
  });

  function submit() {
    const body = text.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  }

  // Défilement : en bas à l'ouverture et à l'arrivée d'un message si l'on y
  // était déjà ; sur place quand des messages précédents s'ajoutent en tête.
  const scroller = useRef<HTMLDivElement>(null);
  const shown = useRef({ newest: 0, oldest: 0 });
  /** Distance entre le haut de la zone visible et le bas du fil. */
  const fromBottom = useRef(0);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) {
      shown.current = { newest: 0, oldest: 0 };
      return;
    }
    const previous = shown.current;
    if (newest !== previous.newest) {
      const wasAtBottom = fromBottom.current - el.clientHeight < 80;
      if (!previous.newest || wasAtBottom || lastIsMine)
        el.scrollTop = el.scrollHeight;
    } else if (oldest !== previous.oldest) {
      el.scrollTop = el.scrollHeight - fromBottom.current;
    }
    shown.current = { newest, oldest };
    fromBottom.current = el.scrollHeight - el.scrollTop;
  }, [newest, oldest, lastIsMine]);

  const conversation = data?.conversation;
  const title = conversation?.title ?? fallbackTitle ?? 'Échange';
  const kind = conversation?.kind ?? fallbackKind;
  const members = data?.members ?? [];

  return (
    <div
      className="messaging-card messaging-conversation h-[calc(100dvh-11rem)] lg:h-[calc(100dvh-9rem)] min-h-[24rem]"
    >
      <header>
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour aux conversations"
          title="Retour aux conversations"
          className="messaging-back"
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
        <div>
          <h2>{title}</h2>
          {conversation && (
            <p>
              {KIND_LABELS[conversation.kind] ?? 'Échange'}
              {conversation.kind === 'GROUP' && members.length > 0
                ? ` · ${members.map((m) => m.name).join(', ')}`
                : ''}
            </p>
          )}
        </div>
      </header>
      {/* Toujours visible, même si le fil est indisponible : un élève lit ici. */}
      <p className="messaging-student-info">
        <GraduationCap size={14} aria-hidden="true" className="flex-shrink-0" />
        {TEXT_ONLY[kind] ?? TEXT_ONLY.DIRECT}
      </p>

      {blocked ? (
        <ErrorState
          message={studentMessagingError(thread.error, OFFLINE)}
          onRetry={() => void thread.refetch()}
        />
      ) : !data ? (
        <Loading label="Chargement des messages…" />
      ) : (
        <>
          {thread.isError && (
            <RefreshNotice
              error={thread.error}
              onRetry={() => void thread.refetch()}
            />
          )}
          <div
            ref={scroller}
            role="log"
            aria-label="Fil des messages"
            onScroll={(e) => {
              const el = e.currentTarget;
              fromBottom.current = el.scrollHeight - el.scrollTop;
            }}
            className="messaging-log relative"
          >
            {data.hasMore && (
              <div className="messaging-more">
                <button
                  type="button"
                  className={secondaryButton}
                  disabled={older.isPending}
                  onClick={() => older.mutate(oldest)}
                >
                  {older.isPending && (
                    <Loader2
                      size={15}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Messages précédents
                </button>
                {older.isError && (
                  <p role="alert" className="messaging-alert">
                    {studentMessagingError(older.error, OFFLINE)}
                  </p>
                )}
              </div>
            )}
            {data.items.length === 0 ? (
              <p className="messaging-empty">
                Aucun message pour le moment. Écrivez le premier.
              </p>
            ) : (
              <ol>
                {data.items.map((message, index) => (
                  <Line
                    key={message.id}
                    message={message}
                    newDay={
                      index === 0 ||
                      !dayjs(data.items[index - 1].createdAt).isSame(
                        message.createdAt,
                        'day',
                      )
                    }
                  />
                ))}
              </ol>
            )}
          </div>

          {data.conversation.canWrite ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="messaging-composer"
            >
              {send.isError && (
                <p role="alert" className="messaging-alert">
                  {studentMessagingError(send.error, OFFLINE)}
                </p>
              )}
              <textarea
                aria-label="Votre message"
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                // Figé pendant l'envoi, sans perdre le clavier du téléphone.
                readOnly={send.isPending}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Rédigez un message…"
              />
              <div className="messaging-composer-tools">
                <span className="messaging-hint hidden sm:block">
                  Entrée pour envoyer · Maj+Entrée pour aller à la ligne
                </span>
                <button
                  type="submit"
                  disabled={!text.trim() || send.isPending}
                  className="messaging-send"
                >
                  {send.isPending ? (
                    <Loader2
                      size={16}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Send size={16} aria-hidden="true" />
                  )}
                  Envoyer
                </button>
              </div>
            </form>
          ) : (
            <p className="messaging-readonly">
              Compte en lecture seule : vous ne pouvez pas répondre.
            </p>
          )}
        </>
      )}
    </div>
  );
}
